'use client';

import { useRef } from 'react';
import { useLocale } from 'next-intl';

import { getDrivesCopy } from '@/i18n/drives-copy';
import { getFolderUploadPanelCopy } from '@/i18n/folder-upload-panel-copy';
import { getUploadLimitsCopy } from '@/i18n/upload-limits-copy';
import {
  folderUploadManager,
  type FolderUploadManager,
} from '@/lib/uploads/folder-upload/manager';

import { FolderUploadJobCard } from './folder-upload-job-card';
import {
  FOLDER_UPLOADS_TRAY_GAP_PX,
  useFolderUploadsTrayBottom,
} from './folder-uploads-tray-offset';
import { useFolderUploadJobs } from './folder-uploads-provider';

/**
 * The bottom-right tray of file and folder uploads (#5823). Every upload's
 * progress, failures, Cancel and Retry live here, never in a toast, and each
 * stays until the member dismisses it.
 */
export function FolderUploadsPanel({
  manager = folderUploadManager,
}: {
  manager?: FolderUploadManager;
}) {
  const locale = useLocale();
  const jobs = useFolderUploadJobs();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Above any chat composer below it; the voice dock steps around the tray.
  const bottom = useFolderUploadsTrayBottom(scrollRef, jobs.length > 0);
  // The lift is a transform so it eases instead of jumping (TASTE §7);
  // `bottom` itself never changes.
  const lift = Math.max(0, bottom - FOLDER_UPLOADS_TRAY_GAP_PX);
  if (jobs.length === 0) return null;
  const copy = getFolderUploadPanelCopy(locale);
  const myDriveLabel = getDrivesCopy(locale).myDrive;
  const limitsCopy = getUploadLimitsCopy(locale);
  return (
    <section
      aria-label={copy.regionLabel}
      data-folder-uploads-panel
      style={{
        bottom: `max(${FOLDER_UPLOADS_TRAY_GAP_PX}px, env(safe-area-inset-bottom))`,
        transform: `translateY(${-lift}px)`,
      }}
      className="pointer-events-none fixed right-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col transition-transform duration-(--imai-duration-swap) ease-(--imai-ease) motion-reduce:transition-none"
    >
      {/* The scroll viewport is inset by the card shadow and pulled back by
          the same amount, so it never clips the elevated cards (TASTE). */}
      <div
        ref={scrollRef}
        style={{ maxHeight: `calc(100dvh - ${bottom}px + 0.5rem)` }}
        className="-m-3 flex min-h-0 flex-col gap-2 overflow-y-auto p-3"
      >
        {jobs.map((job) => (
          <FolderUploadJobCard
            key={job.id}
            job={job}
            copy={copy}
            locale={locale}
            myDriveLabel={myDriveLabel}
            manager={manager}
          />
        ))}
        <p className="bg-popover text-muted-foreground shadow-popover rounded-xl px-3 py-2 text-xs">
          {limitsCopy.folderFormats}
          {jobs.some((job) => job.running) ? ` ${limitsCopy.keepsGoing}` : null}
        </p>
      </div>
    </section>
  );
}
